import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type {
  ActivityId,
  NonConformityId,
  ProjectId,
  ServiceChecklistId,
  ServiceId,
  ServiceVerificationId,
  UserId,
} from '../shared/ids';
import { QUALITY_ERROR, QUALITY_ERROR_MESSAGE } from './errors';
import {
  parseNonConformityDescription,
  parseOptionalNonConformitySolution,
  type NonConformity,
} from './non-conformity';
import type { ServiceChecklist } from './service-checklist';

/**
 * A verificação de qualidade — a FVS respondida numa frente, num dia (Fase 19, fatia 2).
 *
 * ```text
 * 3º Pavimento × Alvenaria · ASSENTAMENTO DE ALVENARIA · 18/09/2026
 * FORM.109 — Execução de Alvenaria Não Estrutural
 *   Marcação da Primeira Fiada     Aprovado
 *   Espessura da Junta             Reprovado → NC nº 48
 *   Requadro de Vãos               Não se aplica
 * verificado por Rafael às 10:32
 * ```
 *
 * ## É a declaração do serviço executado, e é fato do dia (ADR-128)
 *
 * Uma por `(frente, serviço, dia)`. A conferência é por pessoa; a verificação é da frente:
 * duas pessoas na mesma frente respondem uma vez, e quem mede segundo vê a resposta de
 * quem mediu primeiro. Não mora em `DailyReport` — o documento pode nem existir quando a
 * primeira pessoa mede às 9h — e o RDO a **deriva**, como deriva as frentes.
 *
 * ## Ela copia o que responde
 *
 * Nome da disciplina, do serviço, da ficha, e o texto de cada item. A ficha respondida é
 * documento do dia: editar o catálogo depois muda o que será perguntado amanhã, e não o
 * que foi afirmado ontem. É a mesma razão de o RDO copiar o efetivo corrigido.
 *
 * `checklists` vazio é resposta legítima: o serviço declarado não tem ficha, e a
 * declaração por si já satisfaz a RN-218 — quem fez vergas na frente de alvenaria disse o
 * que fez, e não foi obrigado a responder a ficha do assentamento.
 */

export type VerificationResult = 'APPROVED' | 'REJECTED' | 'NOT_APPLICABLE';

export const VERIFICATION_RESULTS: readonly VerificationResult[] = [
  'APPROVED',
  'REJECTED',
  'NOT_APPLICABLE',
];

export const VERIFICATION_RESULT_LABEL: Readonly<Record<VerificationResult, string>> = {
  APPROVED: 'Aprovado',
  REJECTED: 'Reprovado',
  NOT_APPLICABLE: 'Não se aplica',
};

export interface VerificationAnswer {
  readonly itemId: string;
  /** O texto do item, copiado da ficha no instante da resposta (ADR-128). */
  readonly label: string;
  readonly result: VerificationResult;
  /**
   * A NC que este item abriu, nas respostas de antes da Fase 37 (RN-220). Desde a `0084` a
   * ligação mora na NC (`checklistId`, `itemId`), porque o item pode ter uma NC por medição
   * (RN-474) — e esta não é mais escrita.
   */
  readonly nonConformityId?: NonConformityId;
}

export interface VerifiedChecklist {
  readonly checklistId: ServiceChecklistId;
  readonly code?: string;
  readonly name: string;
  readonly answers: readonly VerificationAnswer[];
}

export interface ServiceVerification {
  readonly id: ServiceVerificationId;
  readonly projectId: ProjectId;
  readonly activityId: ActivityId;
  readonly serviceId: ServiceId;
  /** `YYYY-MM-DD` */
  readonly date: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  /** As fichas respondidas. Vazio = serviço declarado sem ficha. */
  readonly checklists: readonly VerifiedChecklist[];
  readonly verifiedByUserId: UserId;
  /** Cópia do nome de quem verificou: o documento não depende de o perfil existir depois. */
  readonly verifiedByName: string;
  readonly verifiedAt: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * Saiu do dia junto com a última frente da frente × serviço (RN-633, `0104`). Fica guardada —
   * a NC cancelada continua apontando para ela —, mas nenhuma leitura do dia a enxerga.
   */
  readonly deletedAt?: string;
}

/** Chave natural: uma verificação viva por `(frente, serviço, dia)` — RN-221. */
export function serviceVerificationNaturalKey(
  activityId: ActivityId,
  serviceId: ServiceId,
  date: string,
): string {
  return `${activityId}|${serviceId}|${date}`;
}

function refuse(code: string, details?: Record<string, unknown>): Result<never, DomainError> {
  return err(domainError(code, QUALITY_ERROR_MESSAGE[code]!, details));
}

/** O que a tela manda por item. */
export interface AnswerInput {
  readonly itemId: string;
  readonly result: VerificationResult;
  /**
   * A NC **desta medição** no item (RN-474). Ausente no item reprovado só quando a
   * reprovação é de outra medição, e esta não encontrou nada ali.
   */
  readonly nonConformity?:
    | {
        readonly description: string;
        readonly solution?: string | undefined;
        /** A quantidade não conforme (RN-473), na unidade do serviço. */
        readonly quantity?: number | undefined;
        /** Os cômodos e as peças do dia onde ela foi encontrada (D9). */
        readonly places?: PlacesRequest | undefined;
      }
    | undefined;
}

/**
 * Os lugares que a tela pede para a NC (D9): as peças pelo nome e os cômodos pela posição e
 * pelo cômodo. Quem os confere contra o dia e guarda o que valia é o caso de uso (RN-517).
 */
export interface PlacesRequest {
  readonly pieces: readonly string[];
  readonly rooms: readonly { readonly positionId: string; readonly roomId: string }[];
}

/** Um item reprovado por esta medição, com o que abrir de NC. */
export interface Rejection {
  readonly checklistId: ServiceChecklistId;
  readonly checklistName: string;
  readonly itemId: string;
  readonly label: string;
  readonly description: string;
  /** O que foi feito para corrigir (RN-294) — opcional desde a Fase 37 (RN-476). */
  readonly solution?: string;
  /** A quantidade não conforme (RN-473). A unidade é conferida pelo caso de uso. */
  readonly quantity?: number;
  /** Os lugares pedidos (D9) — conferidos contra o dia pelo caso de uso (RN-517). */
  readonly places?: PlacesRequest;
}

/**
 * Os itens desta ficha que já têm NC viva de **outra** medição no dia (RN-474): o item → o
 * número da primeira delas, para a mensagem dizer qual.
 */
export type RejectedByOthers = ReadonlyMap<string, number>;

export interface VerifiedChecklistDraft {
  readonly checklist: VerifiedChecklist;
  readonly rejections: readonly Rejection[];
}

/**
 * Confere as respostas de uma ficha (RN-219, RN-220).
 *
 * Todo item responde uma vez: item sem resposta é recusa, e não "aprovado por omissão" —
 * é a diferença entre uma ficha respondida e uma ficha assinada em branco. Item reprovado
 * exige a descrição do que foi encontrado, porque é ela que vira a NC; a solução e a
 * quantidade são opcionais (RN-473, RN-476).
 *
 * O item que já tem NC de **outra** medição no dia (`others`, RN-474) continua reprovado:
 * esta medição pode abrir a sua ali, ou não dizer nada — mas não o aprova por cima da NC de
 * outra pessoa.
 *
 * As respostas saem na ordem da ficha, com o texto do item copiado.
 */
export function verifyChecklist(
  checklist: ServiceChecklist,
  answers: readonly AnswerInput[],
  others: RejectedByOthers = new Map(),
): Result<VerifiedChecklistDraft, DomainError> {
  const porItem = new Map<string, AnswerInput>();
  const itemIds = new Set(checklist.items.map((item) => item.id));

  for (const answer of answers) {
    if (!itemIds.has(answer.itemId)) {
      return refuse(QUALITY_ERROR.VERIFICATION_UNKNOWN_ITEM, {
        checklist: checklist.name,
        itemId: answer.itemId,
      });
    }
    porItem.set(answer.itemId, answer);
  }

  const faltando = checklist.items.filter((item) => !porItem.has(item.id));
  if (faltando.length > 0) {
    return refuse(QUALITY_ERROR.VERIFICATION_INCOMPLETE, {
      checklist: checklist.name,
      missing: faltando.map((item) => item.label),
    });
  }

  const respostas: VerificationAnswer[] = [];
  const rejections: Rejection[] = [];
  for (const item of [...checklist.items].sort((a, b) => a.position - b.position)) {
    const answer = porItem.get(item.id)!;
    const deOutra = others.get(item.id);
    if (answer.result !== 'REJECTED' && deOutra !== undefined) {
      return refuse(QUALITY_ERROR.VERIFICATION_ITEM_HAS_OTHER_NON_CONFORMITY, {
        checklist: checklist.name,
        item: item.label,
        number: deOutra,
      });
    }
    respostas.push({ itemId: item.id, label: item.label, result: answer.result });
    if (answer.result !== 'REJECTED') continue;

    // A reprovação de outra medição, sem nada desta: o item fica reprovado, e esta medição não
    // abre NC nenhuma ali.
    const escrita = answer.nonConformity?.description.trim() ?? '';
    if (escrita === '' && deOutra !== undefined) continue;

    const comItem = (error: DomainError): Result<never, DomainError> =>
      err(
        domainError(error.code, error.message, {
          ...error.details,
          checklist: checklist.name,
          item: item.label,
        }),
      );
    const description = parseNonConformityDescription(escrita);
    if (!description.ok) return comItem(description.error);
    const solution = parseOptionalNonConformitySolution(answer.nonConformity?.solution);
    if (!solution.ok) return comItem(solution.error);
    const quantity = answer.nonConformity?.quantity;
    if (quantity !== undefined && (!Number.isFinite(quantity) || quantity <= 0)) {
      return comItem(
        domainError(
          QUALITY_ERROR.NON_CONFORMITY_QUANTITY_INVALID,
          QUALITY_ERROR_MESSAGE[QUALITY_ERROR.NON_CONFORMITY_QUANTITY_INVALID]!,
          { quantity },
        ),
      );
    }
    // O pedido vazio é "nenhum lugar": não se distingue de não ter pedido.
    const pedido = answer.nonConformity?.places;
    const lugares =
      pedido === undefined || (pedido.pieces.length === 0 && pedido.rooms.length === 0)
        ? undefined
        : pedido;
    rejections.push({
      checklistId: checklist.id,
      checklistName: checklist.name,
      itemId: item.id,
      label: item.label,
      description: description.value,
      ...(solution.value === undefined ? {} : { solution: solution.value }),
      ...(quantity === undefined ? {} : { quantity }),
      ...(lugares === undefined ? {} : { places: lugares }),
    });
  }

  return ok({
    checklist: {
      checklistId: checklist.id,
      name: checklist.name,
      answers: respostas,
      ...(checklist.code === undefined ? {} : { code: checklist.code }),
    },
    rejections,
  });
}

/**
 * Serviço com ficha ativa exige ao menos uma respondida (RN-219).
 *
 * Declarar o serviço e não responder ficha nenhuma, quando há ficha, seria dizer "fiz"
 * sem dizer "ficou bom" — que é exatamente o que a medição passou a perguntar.
 */
export function ensureChecklistAnswered(
  available: readonly ServiceChecklist[],
  answered: readonly VerifiedChecklist[],
  serviceName: string,
): Result<void, DomainError> {
  if (available.length > 0 && answered.length === 0) {
    return refuse(QUALITY_ERROR.VERIFICATION_CHECKLIST_REQUIRED, {
      service: serviceName,
      checklists: available.map((item) => item.name),
    });
  }
  return ok(undefined);
}

export interface OpenVerificationInput {
  readonly id: ServiceVerificationId;
  readonly projectId: ProjectId;
  readonly activityId: ActivityId;
  readonly serviceId: ServiceId;
  readonly date: string;
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly checklists: readonly VerifiedChecklist[];
  readonly by: UserId;
  readonly byName: string;
  readonly now: string;
}

export function openVerification(input: OpenVerificationInput): ServiceVerification {
  return {
    id: input.id,
    projectId: input.projectId,
    activityId: input.activityId,
    serviceId: input.serviceId,
    date: input.date,
    disciplineName: input.disciplineName,
    serviceName: input.serviceName,
    checklists: input.checklists,
    verifiedByUserId: input.by,
    verifiedByName: input.byName,
    verifiedAt: input.now,
    version: 1,
    createdAt: input.now,
    updatedAt: input.now,
  };
}

/**
 * Responder de novo substitui as respostas, sobe a versão e troca o autor (RN-221).
 *
 * Não cria segunda verificação: a chave natural é a mesma, e duas fichas respondidas para
 * a mesma frente no mesmo dia seriam duas verdades sobre o mesmo serviço, sem dizer qual
 * vale. O que foi respondido antes fica na auditoria.
 */
export function replaceVerification(
  existing: ServiceVerification,
  input: {
    readonly checklists: readonly VerifiedChecklist[];
    readonly by: UserId;
    readonly byName: string;
    readonly now: string;
  },
): ServiceVerification {
  return {
    ...existing,
    checklists: input.checklists,
    verifiedByUserId: input.by,
    verifiedByName: input.byName,
    verifiedAt: input.now,
    version: existing.version + 1,
    updatedAt: input.now,
  };
}

/**
 * Tira a resposta de um item da ficha (RN-484) — o "Reprovado" de uma NC cancelada por engano.
 *
 * A reprovação era a NC: cancelada a NC, o item não fica reprovado sem número na ficha do dia,
 * no RDO e na qualidade por serviço. Também não vira "Aprovado", que ninguém verificou: fica
 * sem resposta, e quem medir aquele dia de novo responde o item outra vez. Sem a resposta,
 * devolve o mesmo objeto.
 */
export function withoutAnswer(
  verification: ServiceVerification,
  checklistId: string,
  itemId: string,
  now: string,
): ServiceVerification {
  let mudou = false;
  const checklists = verification.checklists.map((checklist) => {
    if (checklist.checklistId !== checklistId) return checklist;
    const answers = checklist.answers.filter((answer) => answer.itemId !== itemId);
    if (answers.length === checklist.answers.length) return checklist;
    mudou = true;
    return { ...checklist, answers };
  });
  if (!mudou) return verification;
  return { ...verification, checklists, version: verification.version + 1, updatedAt: now };
}

export interface VerificationSummary {
  readonly approved: number;
  readonly rejected: number;
  readonly notApplicable: number;
  readonly total: number;
}

/** `7/7 aprovados`, `1 reprovado` — os números que a tela e o RDO mostram. */
export function summarizeVerification(
  verification: Pick<ServiceVerification, 'checklists'>,
): VerificationSummary {
  let approved = 0;
  let rejected = 0;
  let notApplicable = 0;
  for (const checklist of verification.checklists) {
    for (const answer of checklist.answers) {
      if (answer.result === 'APPROVED') approved += 1;
      else if (answer.result === 'REJECTED') rejected += 1;
      else notApplicable += 1;
    }
  }
  return { approved, rejected, notApplicable, total: approved + rejected + notApplicable };
}

/**
 * As NCs de um item da verificação, de todas as medições (RN-474).
 *
 * Pela ligação da NC (`0084`) e, na resposta de antes da Fase 37, pela que a resposta guardava
 * — a migração copiou uma para a outra, e as duas juntas não deixam nenhuma de fora.
 */
export function nonConformitiesOfAnswer<
  T extends Pick<NonConformity, 'id' | 'verificationId' | 'checklistId' | 'itemId'>,
>(
  ncs: readonly T[],
  verificationId: string,
  checklistId: string,
  answer: Pick<VerificationAnswer, 'itemId' | 'nonConformityId'>,
): T[] {
  return ncs.filter(
    (nc) =>
      (nc.verificationId === verificationId &&
        nc.checklistId === checklistId &&
        nc.itemId === answer.itemId) ||
      (answer.nonConformityId !== undefined && nc.id === answer.nonConformityId),
  );
}

/**
 * Os itens de uma ficha com NC viva de **outra** medição (RN-474) — o item → o menor número.
 *
 * "Outra" é a de outro dono: esta medição não a cancela nem aprova o item por cima dela. A NC
 * sem dono (de antes da `0084`, RN-480) é "outra" para quem tem medição; para quem responde
 * sem medição a quem pertencer, ela é a própria — como era antes da Fase 37.
 */
export function rejectedByOthers(
  ncs: readonly Pick<
    NonConformity,
    | 'verificationId'
    | 'checklistId'
    | 'itemId'
    | 'status'
    | 'number'
    | 'allocationId'
    | 'teamMeasurementId'
  >[],
  verificationId: string | undefined,
  checklistId: string,
  owner: string | undefined,
): RejectedByOthers {
  const itens = new Map<string, number>();
  if (verificationId === undefined) return itens;
  for (const nc of ncs) {
    if (nc.status === 'CANCELLED') continue;
    if (nc.verificationId !== verificationId || nc.checklistId !== checklistId) continue;
    if (nc.itemId === undefined) continue;
    const dono =
      nc.allocationId !== undefined
        ? `A:${nc.allocationId}`
        : nc.teamMeasurementId !== undefined
          ? `T:${nc.teamMeasurementId}`
          : undefined;
    if (dono === owner) continue;
    const atual = itens.get(nc.itemId);
    if (atual === undefined || nc.number < atual) itens.set(nc.itemId, nc.number);
  }
  return itens;
}

export interface NonConformityPlan {
  /** Item que continua reprovado por esta medição: a NC dela fica, com o que se escreveu agora. */
  readonly keep: readonly {
    readonly nonConformityId: NonConformityId;
    readonly rejection: Rejection;
  }[];
  /** Item reprovado por esta medição pela primeira vez: abre NC nova. */
  readonly open: readonly Rejection[];
  /** NC desta medição cujo item deixou de ser reprovado por ela — ou cuja ficha saiu. */
  readonly cancel: readonly NonConformityId[];
}

/**
 * O que fazer com as NCs **desta medição** ao responder de novo (RN-221, RN-474).
 *
 * A NC é do item e da medição: enquanto a medição continuar reprovando o item, é a mesma NC,
 * com o mesmo número. Abrir uma nova a cada resposta encheria o FORM.30 de repetições com
 * números diferentes para a mesma fissura; apagar a antiga faria o número sumir da sequência.
 * Cancelar é o meio-termo que a planilha já pratica: a linha riscada continua lá. As NCs das
 * outras medições não entram aqui — nem para manter, nem para cancelar.
 *
 * `ownPrevious` são as NCs vivas desta medição nesta verificação.
 */
export function planNonConformities(
  ownPrevious: readonly Pick<NonConformity, 'id' | 'checklistId' | 'itemId' | 'status'>[],
  drafts: readonly VerifiedChecklistDraft[],
): NonConformityPlan {
  const keep: { nonConformityId: NonConformityId; rejection: Rejection }[] = [];
  const open: Rejection[] = [];
  const kept = new Set<NonConformityId>();
  const vivas = ownPrevious.filter((nc) => nc.status !== 'CANCELLED');

  for (const draft of drafts) {
    for (const rejection of draft.rejections) {
      const anterior = vivas.find(
        (nc) =>
          !kept.has(nc.id) &&
          nc.checklistId === rejection.checklistId &&
          nc.itemId === rejection.itemId,
      );
      if (anterior === undefined) {
        open.push(rejection);
      } else {
        keep.push({ nonConformityId: anterior.id, rejection });
        kept.add(anterior.id);
      }
    }
  }

  const cancel = vivas.filter((nc) => !kept.has(nc.id)).map((nc) => nc.id);
  return { keep, open, cancel };
}
